export const FLOWS = ['replenishment', 'winback', 'subscription', 'cross-sell'] as const;
export type Flow = typeof FLOWS[number];

export const FLOW_LABELS: Record<Flow, string> = {
  replenishment: 'Replenishment',
  winback: 'Winback',
  subscription: 'Subscription',
  'cross-sell': 'Cross-sell',
};

export type EmailDraft = {
  subject: string;
  previewText: string;
  headline: string;
  body: string;
};

export type Emails = Record<Flow, (EmailDraft | null)[]>;

export const EMPTY_EMAILS: Emails = {
  replenishment: [null, null, null],
  winback: [null, null, null],
  subscription: [null, null, null],
  'cross-sell': [null, null, null],
};

export const LAYOUTS = ['grid', 'hero-supporting', 'horizontal-card', 'stack'] as const;
export type Layout = typeof LAYOUTS[number];
export const LAYOUT_LABELS: Record<Layout, string> = {
  grid: 'Grid',
  'hero-supporting': 'Hero + Supporting',
  'horizontal-card': 'Horizontal Card',
  stack: 'Stack',
};
export type EmailLayouts = Record<Flow, Layout[]>;
export const DEFAULT_LAYOUTS: EmailLayouts = {
  replenishment: ['grid', 'grid', 'grid'],
  winback: ['grid', 'grid', 'grid'],
  subscription: ['grid', 'grid', 'grid'],
  'cross-sell': ['grid', 'grid', 'grid'],
};

export const TONES = ['friendly', 'playful', 'premium', 'minimal', 'professional'] as const;
export type Tone = typeof TONES[number];

// Where a cross-sell email pulls its products from in Klaviyo. Mirrors the
// "Recommendation source" selector in the relo-admin-v2 product-block wizard.
// Only cross-sell emails support a choice — other flows always use the
// prediction event, because their profile properties don't carry product
// objects (e.g. bp_next_prediction_cart is just humanized name strings).
export const RECOMMENDATION_SOURCES = ['event', 'crossell'] as const;
export type RecommendationSource = typeof RECOMMENDATION_SOURCES[number];
export const RECOMMENDATION_SOURCE_LABELS: Record<RecommendationSource, string> = {
  event: 'Prediction event sent to Klaviyo',
  crossell: 'Cross-sell recommendations on the Klaviyo profile',
};

export const CURRENCIES = [
  { code: 'USD', symbol: '$', label: '$ USD' },
  { code: 'GBP', symbol: '£', label: '£ GBP' },
  { code: 'EUR', symbol: '€', label: '€ EUR' },
  { code: 'AUD', symbol: 'A$', label: 'A$ AUD' },
  { code: 'CAD', symbol: 'C$', label: 'C$ CAD' },
  { code: 'NZD', symbol: 'NZ$', label: 'NZ$ NZD' },
  { code: 'JPY', symbol: '¥', label: '¥ JPY' },
  { code: 'SEK', symbol: 'kr', label: 'kr SEK' },
  { code: 'AED', symbol: 'AED', label: 'AED' },
] as const;
export type CommonCurrencyCode = typeof CURRENCIES[number]['code'];

export const isCurrencyCode = (value: string): value is CommonCurrencyCode => CURRENCIES
  .some((c) => c.code === value);

export const normalizeCurrencyCode = (value: string) => value.trim().toUpperCase();

// Returns the display symbol for a currency code (e.g. "GBP" -> "£").
// Falls back to the normalized code for currencies we don't have a symbol for,
// matching the merchant-default handling in SettingsPanel.
export const currencySymbolFor = (value: string): string => {
  const normalized = normalizeCurrencyCode(value);
  const match = CURRENCIES.find((c) => c.code === normalized);
  return match ? match.symbol : normalized;
};

export type Settings = {
  brandName: string;
  discountCode: string;
  currencyCode: string;
  replenishmentButtonText: string;
  subscriptionButtonText: string;
  crossSellButtonText: string;
  crossSellHeader: string;
  crossSellRecommendationSource: RecommendationSource;

  primaryColor: string;
  backgroundColor: string;
  buttonTextColor: string;
  textColor: string;
  buttonBorderRadius: number;
  productImageMaxHeightDesktop: number;
  productImageMaxHeightMobile: number;

  existingEmailCopy: string;
  tone: Tone;
};

export const DEFAULT_SETTINGS: Settings = {
  brandName: '',
  discountCode: '',
  currencyCode: 'USD',
  replenishmentButtonText: 'Reorder now',
  subscriptionButtonText: 'Subscribe & save',
  crossSellButtonText: 'Add to cart',
  crossSellHeader: 'You might also like',
  crossSellRecommendationSource: 'event',

  primaryColor: '#111111',
  backgroundColor: '#ffffff',
  buttonTextColor: '#ffffff',
  textColor: '#222222',
  buttonBorderRadius: 4,
  productImageMaxHeightDesktop: 240,
  productImageMaxHeightMobile: 180,

  existingEmailCopy: '',
  tone: 'friendly',
};
